"use client";

import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Pagination } from "antd";
import { urlFor } from "@/sanity/lib/image";
import {
  getBannerPost,
  searchAndFilterPaginatedPosts,
} from "@/sanity/actions/blogActions";

import HeroSection from "@/components/common/HeroSection";
import Search from "@/components/blog/Search";
import BlogBanner from "@/components/blog/BlogBanner";
import BlogCard from "@/components/blog/BlogCard";
import { BlogBannerSkeleton } from "@/components/skeletons/BlogBannerSkeleton";
import { BlogCardSkeleton } from "@/components/skeletons/BlogCardSkeleton";

export default function BlogPageClient() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [bannerPost, setBannerPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "",
    tag: searchParams.get("tag") || "",
    date: searchParams.get("date") || "",
  });
  const [totalItems, setTotalItems] = useState(0);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);

  const currentPage = Number(searchParams.get("page")) || 1;
  const cardsPerPage = 18;

  const getImageUrl = (image, width = 800, height = 600, quality = 80) => {
    try {
      if (!image) return "/assets/fallbacks/blogFallback.webp";
      return urlFor(image)
        .width(width)
        .height(height)
        .quality(quality)
        .auto("format")
        .url();
    } catch (e) {
      return "/assets/fallbacks/blogFallback.webp";
    }
  };

  const getAvatarUrl = (image, size = 48, quality = 80) => {
    try {
      if (!image) return "/assets/fallbacks/avatar-fallback.svg";
      return urlFor(image)
        .width(size)
        .height(size)
        .quality(quality)
        .auto("format")
        .url();
    } catch (e) {
      return "/assets/fallbacks/avatar-fallback.svg";
    }
  };

  useEffect(() => {
    async function loadBanner() {
      setBannerLoading(true);
      const banner = await getBannerPost(filters.category);
      setBannerPost(banner);
      setBannerLoading(false);
    }
    loadBanner();
  }, [filters.category]);

  useEffect(() => {
    (async () => {
      setPostsLoading(true);
      const { posts, total } = await searchAndFilterPaginatedPosts(
        filters,
        currentPage,
        cardsPerPage
      );
      setPosts(posts);
      setTotalItems(total);
      setPostsLoading(false);
    })();
  }, [filters, currentPage]);

  return (
    <div className="relative flex flex-col justify-center items-center mt-6 mx-2 sm:mx-5 md:mx-[40px] xl:mx-[100px] 3xl:mx-[150] z-10">
      <HeroSection
        heading="BLOGS"
        subHeading="Let's Build What's Next"
        paragraph="Stay informed with the latest industry insights, technology trends, and expert advice. Our blog covers everything from software development best practices to AI innovations, helping businesses stay ahead in 
                      the digital world."
        headingStyles="text-[70px] mobile:text-[120px] sm:text-[150px] md:text-[180px] lg:text-[210px] tablet:text-[220px] xl:text-[230px] laptop:text-[230px] 3xl:text-[290px] 4xl:text-[330px] leading-[100%] text-center opacity-100 "
        scrollTarget="blog"
      />

      <div id="blog" className="mt-16 w-full ">
        <Search 
          filters={filters}
          onChange={(newFilters) => {
            setFilters(newFilters);
            
            const params = new URLSearchParams(searchParams.toString());
            Object.entries(newFilters).forEach(([key, value]) => {
              if (value) {
                params.set(key, value);
              } else {
                params.delete(key);
              }
            });
            params.set("page", "1");
            router.push(`?${params.toString()}`, { scroll: false });
          }} 
        />
      </div>

      {bannerLoading ? (
        <>
          {/* Banner Skeleton */}
          <div className="mt-16 w-full">
            <BlogBannerSkeleton />
          </div>
        </>
      ) : (
        <>
          {/* Featured Banner */}
          {bannerPost && (
            <div className="mt-16 w-full">
              <BlogBanner
                image={getImageUrl(bannerPost.mainImage, 702, 314, 80)}
                blurDataURL={bannerPost.mainImage?.asset?.metadata?.lqip}
                author={bannerPost.authorName}
                authorSlug={bannerPost.authorSlug}
                authorImage={getAvatarUrl(bannerPost.authorImage, 48, 80)}
                date={
                  bannerPost.publishedAt
                    ? new Date(bannerPost.publishedAt).toDateString()
                    : ""
                }
                title={bannerPost.title}
                description={bannerPost.excerpt || ""}
                slug={bannerPost.slug}
              />
            </div>
          )}
        </>
      )}
      {/* Grid of cards */}
      <div className="mt-16 w-full">
        <div className="grid grid-cols-1 mobile:grid-cols-2 md:grid-cols-3 tablet:grid-cols-3 gap-4 mobile:gap-3 sm:gap-4 md:gap-3 tablet:gap-6 lg:gap-10 2xl:gap-20 3xl:gap-30 max-w-[1800px] mx-auto">
          {postsLoading ? (
            <>
              {/* Cards Skeleton */}
              {Array.from({ length: 6 }).map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </>
          ) : (
            posts.map((card) => (
              <BlogCard
                key={card._id}
                image={getImageUrl(card.mainImage, 370, 173, 80)}
                blurDataURL={card.mainImage?.asset?.metadata?.lqip}
                author={card.authorName}
                authorSlug={card.authorSlug}
                authorImage={getAvatarUrl(card.authorImage, 48, 80)}
                date={
                  card.publishedAt
                    ? new Date(card.publishedAt).toDateString()
                    : ""
                }
                title={card.title}
                description={card.excerpt || ""}
                slug={card.slug}
                tags={card.tags?.map((t) => t.title) || []}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex justify-center mt-10 mb-10 px-2">
        <Pagination
          current={currentPage}
          pageSize={cardsPerPage}
          total={totalItems}
          onChange={(page) => {
            const params = new URLSearchParams(searchParams.toString());
            params.set("page", page.toString());
            router.push(`?${params.toString()}`);
          }}
          showSizeChanger={false}
          // responsive={true}
          className="custom-pagination"
        />
      </div>
    </div>
  );
}
